import React from 'react';
import Tooltip from '@mui/material/Tooltip';

import Table from '~/src/containers/Table';
import { RowActions } from './RowActions';

import { type Questionnaire } from '@clio/questionnaire-builder';
import { getFormattedDateFromIsoString } from '~/src/utils/date';

const outdatedBadgeStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: '4px',
  backgroundColor: '#FEF3C7',
  color: '#92400E',
  border: '1px solid #FDE68A',
  whiteSpace: 'nowrap',
};

export const Questionnaires = ({
  hideHeader,
  empty,
  noBorder,
  visibleItems,
  questionnaires,
  handleEditQuestionnaire,
  handleShareQuestionnaire,
  handleRenameQuestionnaire,
  handleDuplicateQuestionnaire,
  handleDeleteQuestionnaire,
  questionnaireDocumentMapping,
  questionnaireDeprecatedMapping,
}: {
  hideHeader?: boolean;
  empty?: boolean;
  noBorder?: boolean;
  visibleItems: number;
  questionnaires: Questionnaire[];
  handleEditQuestionnaire: (questionnaire: Questionnaire) => void;
  handleShareQuestionnaire: (
    questionnaire: Questionnaire & { total_questions: number },
  ) => void;
  handleRenameQuestionnaire: (questionnaire: Questionnaire) => void;
  handleDuplicateQuestionnaire: (questionnaire: Questionnaire) => void;
  handleDeleteQuestionnaire: (questionnaire: Questionnaire) => void;
  questionnaireDocumentMapping: { [key: string]: number };
  questionnaireDeprecatedMapping: Record<
    string,
    { title: string | null; subtitle: string }[]
  >;
}) => {
  const rowActions = {
    edit: handleEditQuestionnaire,
    share: handleShareQuestionnaire,
    rename: handleRenameQuestionnaire,
    duplicate: handleDuplicateQuestionnaire,
    delete: handleDeleteQuestionnaire,
  };

  const renderRowAction = (
    questionnaire: Questionnaire & { total_questions: number },
  ) => <RowActions actions={rowActions} questionnaire={questionnaire} />;

  return (
    <>
      <Table
        hideHeader={hideHeader}
        noBorder={noBorder}
        emptyTitle={'No questionnaires found.'}
        empty={empty}
        isLoading={false}
        renderActions={() => <div />}
        data={questionnaires}
        visibleItems={visibleItems}
        columns={[
          {
            label: 'Template name',
            dataKey: 'title',
            style: {
              width: '40%',
              textAlign: 'left',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            },
            render: (
              title: string,
              { rowData }: { rowData: Questionnaire },
            ) => {
              const deprecatedList =
                questionnaireDeprecatedMapping[String(rowData.id)];
              const isDeprecated = deprecatedList && deprecatedList.length > 0;
              return (
                <Tooltip
                  title={
                    isDeprecated
                      ? 'One or more forms in this questionnaire is out of date or no longer offered. Please check your court forms library, and create a new questionnaire.'
                      : ''
                  }
                  placement="top-end"
                  disableHoverListener={!isDeprecated}
                >
                  <div className="flex items-center gap-2">
                    <span>{title}</span>
                    {isDeprecated && (
                      <span style={outdatedBadgeStyle}>
                        Questionnaire out of date
                      </span>
                    )}
                  </div>
                </Tooltip>
              );
            },
          },
          {
            label: 'Questions',
            dataKey: 'total_questions',
            style: { width: '10%', textAlign: 'left' },
          },
          {
            label: 'Documents',
            dataKey: 'id',
            style: { width: '10%', textAlign: 'left' },
            render: (value: number) => {
              return questionnaireDocumentMapping[value] || 0;
            },
          },
          {
            label: 'Last updated',
            dataKey: 'last_modified_at',
            style: { width: '20%', textAlign: 'left' },
            render: (value: string) => {
              return <span>{getFormattedDateFromIsoString(value)}</span>;
            },
          },
          {
            label: 'Actions',
            hideLabel: true,
            render: renderRowAction,
            style: {
              width: '20%',
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'flex-end',
            },
          },
        ]}
      />
    </>
  );
};
